'use client';

import type { AnswerTemplatesBindings } from '@/components/answer-templates/use-answer-templates';
import AutoRepliesPanel from '@/components/AutoRepliesPanel';
import { AnswerTemplatesEditorContentFromBindings } from '@/components/modal-answer-picked/ModalAnswerTemplatesEditor';
import { SettingsMaxNotificationsContent } from '@/components/settings-max-notifications-modal/SettingsMaxNotificationsContent';
import { SettingsMaxNotificationsModalControllerBindings } from '@/components/settings-max-notifications-modal/use-settings-max-notifications-modal-controller';
import {
    SETTINGS_TABS,
    SettingsTabId,
    isEditableSettingsTab,
} from '@/components/settings-questionnaire-modal/settings-questionnaire.schema';
import {
    SettingsQuestionnaireSection,
    SettingsSectionCard,
} from '@/components/settings-questionnaire-modal/SettingsQuestionnaireSections';
import { SettingsQuestionnaireModalControllerBindings } from '@/components/settings-questionnaire-modal/use-settings-questionnaire-modal-controller';
import {
    ClientBillingOperationHistoryDailyRowEntity,
    ClientPaymentRecordEntity,
} from '@/entities/client-billing.entity';
import { UIPriority } from '@/enums/ui-priorities';
import { getClientBillingRemainingTokens } from '@/shared/client-billing';
import {
    CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEY_ORDER,
    CLIENT_BILLING_OPERATION_HISTORY_USAGE_LABELS_RU,
} from '@/shared/client-billing-operations';
import Button from '@/ui/Button';
import Input from '@/ui/Input';
import Modal from '@/ui/Modal';
import {
    DEFAULT_BILLING_CURRENCY,
    formatBillingTokenAmount,
    formatMoneyMinorUnits,
    type BillingCurrency,
} from '@mrw/billing-money';
import clsx from 'clsx';
import { FC, ReactNode } from 'react';

type SettingsQuestionnaireModalViewProps = SettingsQuestionnaireModalControllerBindings & {
    isShown: boolean;
    setIsShown: (val: boolean) => void;
    activeTab: SettingsTabId;
    setActiveTab: (tabId: SettingsTabId) => void;
    maxNotifications: SettingsMaxNotificationsModalControllerBindings;
    answerTemplates: AnswerTemplatesBindings;
};

const paymentColumns = ['ID операции', 'Дата', 'Email', 'Сумма', 'Метод, Статус', 'Токены', 'Документы'];
const packageColumns = ['Пакет', 'Токены', 'Цена', ''];
const limitColumns = ['Операция', 'Расход', 'Использовано (шт)', 'Использовано (токенов)'];
const operationHistoryStaticColumns = [
    'Дата отчета',
    'Баланс на конец дня',
    'Итого сводная за день',
    'Пополнение баланса в токенах',
];
const operationHistoryUsageGroupLabel = 'Расход токенов (суммарно за день по каждой операции):';

const SETTINGS_NUMBER_LOCALE = 'ru-RU';

const formatCountAmount = (value: number): string =>
    new Intl.NumberFormat(SETTINGS_NUMBER_LOCALE, {
        maximumFractionDigits: 3,
    }).format(value);
const formatTokenAmount = (value: number): string =>
    formatBillingTokenAmount(value, {
        intlLocale: SETTINGS_NUMBER_LOCALE,
    });
const getRussianTokenWord = (value: number): 'токен' | 'токена' | 'токенов' => {
    const rounded = Math.abs(Math.trunc(value));
    const lastTwoDigits = rounded % 100;
    const lastDigit = rounded % 10;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return 'токенов';
    }

    if (lastDigit === 1) {
        return 'токен';
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
        return 'токена';
    }

    return 'токенов';
};
const formatTokenCost = (tokenCost: number): string => {
    if (!Number.isFinite(tokenCost) || tokenCost <= 0) {
        return `0 ${getRussianTokenWord(0)}`;
    }

    if (!Number.isInteger(tokenCost)) {
        return `${formatCountAmount(1 / tokenCost)} шт. за 1 токен`;
    }

    return `${formatCountAmount(tokenCost)} ${getRussianTokenWord(tokenCost)}`;
};
const formatMoneyAmount = (minorUnits: number, currency: BillingCurrency = DEFAULT_BILLING_CURRENCY): string =>
    formatMoneyMinorUnits(minorUnits, {
        intlLocale: SETTINGS_NUMBER_LOCALE,
        currency,
    });
const formatPaymentDate = (value: string): string => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU');
};
const formatPercent = (value: number | null): string =>
    value === null
        ? 'Нет данных'
        : `${new Intl.NumberFormat(SETTINGS_NUMBER_LOCALE, { maximumFractionDigits: 1 }).format(value)}%`;
const formatHistoryDay = (value: string): string => {
    const [year, month, day] = value.split('-');
    return year && month && day ? `${day}.${month}.${year}` : value;
};
const formatSignedTokenDelta = (value: number): string => {
    if (value > 0) {
        return `+${formatTokenAmount(value)}`;
    }

    if (value < 0) {
        return `-${formatTokenAmount(Math.abs(value))}`;
    }

    return formatTokenAmount(0);
};

const SettingsDataTable: FC<{
    columns: string[];
    emptyMessage: string;
    rows?: ReactNode[][];
}> = ({ columns, emptyMessage, rows = [] }) => {
    return (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                <thead className="bg-slate-50">
                    <tr>
                        {columns.map((column) => (
                            <th key={column} className="px-4 py-3 font-medium text-slate-700">
                                {column}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                    {rows.length === 0 ? (
                        <tr>
                            <td className="px-4 py-6 text-sm text-slate-500" colSpan={columns.length}>
                                {emptyMessage}
                            </td>
                        </tr>
                    ) : (
                        rows.map((cells, rowIndex) => (
                            <tr key={rowIndex}>
                                {cells.map((cell, cellIndex) => (
                                    <td key={cellIndex} className="px-4 py-3 text-slate-700">
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

const SettingsOperationHistoryTable: FC<{
    rows: ClientBillingOperationHistoryDailyRowEntity[];
}> = ({ rows }) => {
    const totalColumnCount =
        operationHistoryStaticColumns.length + CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEY_ORDER.length;

    return (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-full border-collapse text-left text-sm">
                <thead className="bg-slate-50">
                    <tr>
                        {operationHistoryStaticColumns.map((column) => (
                            <th
                                key={column}
                                rowSpan={2}
                                scope="col"
                                className="border border-slate-200 px-3 py-2 align-middle font-medium text-slate-700"
                            >
                                {column}
                            </th>
                        ))}
                        <th
                            colSpan={CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEY_ORDER.length}
                            scope="colgroup"
                            className="border border-slate-200 px-3 py-2 text-center font-medium text-slate-700"
                        >
                            {operationHistoryUsageGroupLabel}
                        </th>
                    </tr>
                    <tr>
                        {CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEY_ORDER.map((usageKey) => (
                            <th
                                key={usageKey}
                                scope="col"
                                className="border border-slate-200 px-3 py-2 font-medium text-slate-700"
                            >
                                {CLIENT_BILLING_OPERATION_HISTORY_USAGE_LABELS_RU[usageKey]}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white">
                    {rows.length === 0 ? (
                        <tr>
                            <td
                                className="border border-slate-100 px-4 py-6 text-sm text-slate-500"
                                colSpan={totalColumnCount}
                            >
                                Операций пока нет.
                            </td>
                        </tr>
                    ) : (
                        rows.map((row) => (
                            <tr key={row.day}>
                                <td className="border border-slate-100 px-3 py-2 text-slate-700">
                                    {formatHistoryDay(row.day)}
                                </td>
                                <td className="border border-slate-100 px-3 py-2 text-slate-700">
                                    {formatTokenAmount(row.balanceAtEndOfDay)}
                                </td>
                                <td className="border border-slate-100 px-3 py-2 text-slate-700">
                                    {formatSignedTokenDelta(row.totalTokenDelta)}
                                </td>
                                <td className="border border-slate-100 px-3 py-2 text-slate-700">
                                    {formatTokenAmount(row.topUpTokens)}
                                </td>
                                {CLIENT_BILLING_OPERATION_HISTORY_USAGE_KEY_ORDER.map((usageKey) => (
                                    <td key={usageKey} className="border border-slate-100 px-3 py-2 text-slate-700">
                                        {formatTokenAmount(row.usage[usageKey])}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

const buildPaymentRows = (payments: ClientPaymentRecordEntity[]): ReactNode[][] =>
    payments.map((payment) => [
        payment.operationId,
        formatPaymentDate(payment.paidAt),
        payment.email,
        formatMoneyAmount(payment.amountMinorUnits, payment.currency),
        `${payment.method}, ${payment.status}`,
        formatCountAmount(payment.tokenAmount),
        payment.documents.length > 0 ? payment.documents.join(', ') : '-',
    ]);

const SettingsAnswerKindPercentChart: FC<{
    points: SettingsQuestionnaireModalControllerBindings['formState']['answerKinds']['daily'];
}> = ({ points }) => {
    const visiblePoints = points.filter((point) => point.aiUnchangedPercent !== null).slice(-30);
    const width = 680;
    const height = 160;
    const barGap = 4;
    const barWidth = visiblePoints.length
        ? Math.max(6, (width - barGap * (visiblePoints.length - 1)) / visiblePoints.length)
        : 0;

    return (
        <figure className="m-0" aria-label="Доля ИИ-ответов без изменений по дням">
            <svg
                className="block w-full"
                viewBox={`0 0 ${width} ${height}`}
                role="img"
                aria-label="Доля ИИ-ответов без изменений по дням"
            >
                <rect width={width} height={height} fill="#f8fafc" rx="8" />
                {visiblePoints.length === 0 ? (
                    <text x={width / 2} y={height / 2} textAnchor="middle" fill="#64748b" fontSize="14">
                        Нет данных
                    </text>
                ) : (
                    visiblePoints.map((point, index) => {
                        const value = point.aiUnchangedPercent ?? 0;
                        const barHeight = Math.max(3, (value / 100) * (height - 36));
                        const x = index * (barWidth + barGap);
                        const y = height - barHeight - 20;

                        return (
                            <g key={point.day}>
                                <rect x={x} y={y} width={barWidth} height={barHeight} fill="#0f766e" rx="2" />
                                {index === 0 || index === visiblePoints.length - 1 ? (
                                    <text x={x} y={height - 6} fill="#475569" fontSize="10">
                                        {point.day.slice(5)}
                                    </text>
                                ) : null}
                            </g>
                        );
                    })
                )}
            </svg>
        </figure>
    );
};

export const SettingsQuestionnaireModalView: FC<SettingsQuestionnaireModalViewProps> = ({
    isShown,
    setIsShown,
    activeTab,
    setActiveTab,
    maxNotifications,
    answerTemplates,
    formState,
    paymentForm,
    questions,
    isLoading,
    isSaving,
    saveConflict,
    dirtyTabIds,
    completionProgress,
    onStoreNameChanged,
    onCompanyInfoLinkChanged,
    onEmailChanged,
    onCommentsChanged,
    onAnswerChanged,
    onGeneralSettingChanged,
    onChannelChanged,
    onMarketplaceReviewImportLimitChanged,
    openPaymentForm,
    closePaymentForm,
    onPaymentPackageChanged,
    onPaymentEmailChanged,
    submitPayment,
    saveForm,
    resetForm,
    closeModal,
    reloadSettings,
}) => {
    const paymentTitleId = 'settings-payment-title';
    const isActiveTabEditable = isEditableSettingsTab(activeTab);
    const remainingTokens = getClientBillingRemainingTokens(formState.billing);
    const dirtyTabSet = new Set(dirtyTabIds);

    const renderHeaderContent = () => (
        <div
            data-settings-limit-header="true"
            className="flex min-w-0 items-center justify-start gap-1.5 text-xs leading-4 text-slate-700"
        >
            <span className="min-w-0 truncate">
                <span>Общий лимит:</span>{' '}
                <span className="font-semibold text-blue-700">
                    {formatTokenAmount(remainingTokens)} отзывов / токенов
                </span>
            </span>
            <button
                type="button"
                aria-label="+ пополнить"
                className="inline-flex shrink-0 items-center gap-1 bg-transparent p-0 text-xs font-medium text-slate-500 hover:text-blue-700 disabled:cursor-default disabled:text-slate-400"
                disabled={formState.billing.packages.length === 0 || paymentForm.isSubmitting}
                onClick={() => openPaymentForm()}
            >
                <span
                    aria-hidden="true"
                    className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold leading-none text-white"
                >
                    +
                </span>
                <span>пополнить</span>
            </button>
        </div>
    );

    const renderPaymentDialog = () => {
        if (!paymentForm.isOpen) {
            return null;
        }

        return (
            <div className="fixed inset-0 z-[1030] flex items-center justify-center bg-black/50 px-3">
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={paymentTitleId}
                    className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl"
                >
                    <div>
                        <h3 id={paymentTitleId} className="text-base font-semibold text-slate-900">
                            Пополнить баланс
                        </h3>
                    </div>
                    {paymentForm.validationMessage ? (
                        <div
                            role="alert"
                            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                        >
                            {paymentForm.validationMessage}
                        </div>
                    ) : null}
                    <div className="mt-4 grid gap-3">
                        <label className="space-y-1 text-sm text-slate-700">
                            <span className="font-medium text-slate-900">Выберите пакет токенов</span>
                            <select
                                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                                value={paymentForm.packageId}
                                onChange={(event) => onPaymentPackageChanged(event.target.value)}
                            >
                                {formState.billing.packages.map((billingPackage) => (
                                    <option key={billingPackage.id} value={billingPackage.id}>
                                        {formatCountAmount(billingPackage.tokenQuantity)} токенов ·{' '}
                                        {formatMoneyAmount(billingPackage.priceMinorUnits, billingPackage.currency)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="space-y-1 text-sm text-slate-700">
                            <span className="font-medium text-slate-900">Email для отправки чека</span>
                            <Input
                                aria-label="Email для отправки чека"
                                type="email"
                                value={paymentForm.email}
                                onChange={(event) => onPaymentEmailChanged(event.target.value)}
                            />
                        </label>
                        <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                            <div>
                                <div className="text-xs text-slate-500">Стоимость</div>
                                <div className="mt-1 font-medium text-slate-900">
                                    {formatMoneyAmount(paymentForm.amountMinorUnits, paymentForm.currency)}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Количество</div>
                                <div className="mt-1 font-medium text-slate-900">
                                    {formatCountAmount(paymentForm.tokenAmount)} токенов
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end gap-2">
                        <Button
                            type="button"
                            priorityType={UIPriority.SECONDARY}
                            uiSize="compact"
                            className="w-28"
                            onClick={closePaymentForm}
                            disabled={paymentForm.isSubmitting}
                        >
                            Отмена
                        </Button>
                        <Button
                            type="button"
                            priorityType={UIPriority.PRIMARY}
                            uiSize="compact"
                            className="w-28"
                            onClick={submitPayment}
                            disabled={paymentForm.isSubmitting || !paymentForm.packageId}
                        >
                            {paymentForm.isSubmitting ? 'Оплата...' : 'Оплатить'}
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    const renderActiveTab = () => {
        if (isLoading) {
            return (
                <div className="h-full min-h-40 flex items-center justify-center text-sm text-slate-500">
                    Загрузка настроек...
                </div>
            );
        }

        if (isEditableSettingsTab(activeTab)) {
            return (
                <SettingsQuestionnaireSection
                    activeTab={activeTab}
                    formState={formState}
                    questions={questions}
                    completionProgress={completionProgress}
                    onStoreNameChanged={onStoreNameChanged}
                    onCompanyInfoLinkChanged={onCompanyInfoLinkChanged}
                    onEmailChanged={onEmailChanged}
                    onCommentsChanged={onCommentsChanged}
                    onAnswerChanged={onAnswerChanged}
                    onGeneralSettingChanged={onGeneralSettingChanged}
                    onChannelChanged={onChannelChanged}
                    onMarketplaceReviewImportLimitChanged={onMarketplaceReviewImportLimitChanged}
                />
            );
        }

        if (activeTab === 'answer-templates') {
            return (
                <AnswerTemplatesEditorContentFromBindings
                    bindings={answerTemplates}
                    showTitle
                    className="h-full min-h-0 w-full"
                />
            );
        }

        if (activeTab === 'auto-replies') {
            return <AutoRepliesPanel />;
        }

        if (activeTab === 'limits-payments') {
            const usageByOperation = new Map(
                formState.billing.usage.operations.map((operationUsage) => [operationUsage.operation, operationUsage]),
            );
            const limitRows = [
                ...formState.billing.operationCosts.map((operationCost) => {
                    const operationUsage = usageByOperation.get(operationCost.operation);

                    return {
                        label: operationCost.displayName,
                        limit: formatTokenCost(operationCost.tokenCost),
                        usageCount: formatCountAmount(operationUsage?.unitQuantity ?? 0),
                        value: formatTokenAmount(operationUsage?.tokenAmount ?? 0),
                    };
                }),
                {
                    label: 'Итого:',
                    limit: '',
                    usageCount: '',
                    value: formatTokenAmount(formState.billing.usage.tokens),
                },
            ];
            const packageRows = formState.billing.packages.map((billingPackage, index) => [
                `${index + 1}.`,
                formatCountAmount(billingPackage.tokenQuantity),
                formatMoneyAmount(billingPackage.priceMinorUnits, billingPackage.currency),
                <Button
                    key={billingPackage.id}
                    type="button"
                    priorityType={UIPriority.SECONDARY}
                    uiSize="compact"
                    className="w-28"
                    disabled={paymentForm.isSubmitting}
                    onClick={() => openPaymentForm(billingPackage.id)}
                >
                    Пополнить
                </Button>,
            ]);
            const paymentRows = buildPaymentRows(formState.billing.payments);

            return (
                <div className="space-y-4">
                    <SettingsSectionCard title="Лимиты">
                        <SettingsDataTable
                            columns={limitColumns}
                            emptyMessage="Лимитов пока нет."
                            rows={limitRows.map((row) => [row.label, row.limit, row.usageCount, row.value])}
                        />
                    </SettingsSectionCard>

                    <SettingsSectionCard title="Пакеты" description="Доступные пакеты загружаются из базы данных.">
                        <SettingsDataTable
                            columns={packageColumns}
                            emptyMessage="Пакетов пока нет."
                            rows={packageRows}
                        />
                    </SettingsSectionCard>

                    <SettingsSectionCard
                        title="Платежи"
                        description="История тестовых платежей хранится по текущему клиенту."
                    >
                        <SettingsDataTable
                            columns={paymentColumns}
                            emptyMessage="Платежей пока нет."
                            rows={paymentRows}
                        />
                    </SettingsSectionCard>
                </div>
            );
        }

        if (activeTab === 'statistics') {
            const answerKindRows = [
                {
                    label: 'ИИ-ответы без изменений',
                    value: formatCountAmount(formState.answerKinds.totals.aiUnchanged),
                },
                {
                    label: 'ИИ-ответы с правками',
                    value: formatCountAmount(formState.answerKinds.totals.aiChanged),
                },
                {
                    label: 'Ответы человека',
                    value: formatCountAmount(formState.answerKinds.totals.human),
                },
                {
                    label: 'Ответы из шаблонов',
                    value: formatCountAmount(formState.answerKinds.totals.template),
                },
            ];

            return (
                <SettingsSectionCard
                    title="Ответы ИИ без изменений"
                    description="Доля отправленных ИИ-ответов, которые не редактировались перед отправкой."
                >
                    <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
                        <div className="space-y-3">
                            <div>
                                <div className="text-xs uppercase tracking-wide text-slate-500">ИИ без изменений</div>
                                <div className="mt-1 text-2xl font-semibold text-slate-900">
                                    {formatPercent(formState.answerKinds.aiUnchangedPercent)}
                                </div>
                            </div>
                            <div className="overflow-hidden rounded-xl border border-slate-200">
                                <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {answerKindRows.map((row) => (
                                            <tr key={row.label}>
                                                <td className="px-4 py-3 text-slate-900">{row.label}</td>
                                                <td className="px-4 py-3 text-slate-600">{row.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <SettingsAnswerKindPercentChart points={formState.answerKinds.daily} />
                    </div>
                </SettingsSectionCard>
            );
        }

        if (activeTab === 'operations-history') {
            return (
                <SettingsSectionCard
                    title="История операций"
                    description="Пополнение баланса и расходы по операциям в токенах с группировкой по дням фактического использования сервиса."
                >
                    <SettingsOperationHistoryTable rows={formState.billing.operationHistoryDaily} />
                </SettingsSectionCard>
            );
        }

        return (
            <div className="space-y-4">
                <SettingsSectionCard title="MAX">
                    <SettingsMaxNotificationsContent {...maxNotifications} />
                </SettingsSectionCard>
            </div>
        );
    };

    return (
        <Modal
            isShown={isShown}
            setIsShown={setIsShown}
            title="Общий лимит"
            headerContent={renderHeaderContent()}
            headerContentPlacement="left"
            closable
            className="!h-[calc(100vh-6px)] !w-[calc(100vw-6px)] max-w-none"
        >
            <form className="w-full h-full min-h-0 flex flex-col" onSubmit={(event) => saveForm(event, activeTab)}>
                <div className="flex-1 min-h-0 overflow-hidden p-[3px]">
                    <div className="flex h-full min-h-0 flex-col gap-[3px]">
                        <div className="shrink-0">
                            <div
                                className="flex gap-[3px] overflow-x-auto pb-[3px]"
                                role="tablist"
                                aria-label="Разделы настроек"
                            >
                                {SETTINGS_TABS.map((tab) => {
                                    const isActive = activeTab === tab.id;
                                    const isDirty = isEditableSettingsTab(tab.id) && dirtyTabSet.has(tab.id);

                                    return (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            role="tab"
                                            aria-selected={isActive}
                                            aria-controls={`settings-tabpanel-${tab.id}`}
                                            id={`settings-tab-${tab.id}`}
                                            className={clsx(
                                                'min-w-fit rounded-lg border px-3 py-2 text-left text-sm transition',
                                                isActive
                                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300',
                                            )}
                                            onClick={() => setActiveTab(tab.id)}
                                        >
                                            <span>{tab.label}</span>
                                            {isDirty ? (
                                                <span
                                                    aria-hidden="true"
                                                    title="Есть несохранённые изменения"
                                                    className="ml-1 font-semibold text-amber-600"
                                                >
                                                    •
                                                </span>
                                            ) : null}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto">
                            <div
                                role="tabpanel"
                                id={`settings-tabpanel-${activeTab}`}
                                aria-labelledby={`settings-tab-${activeTab}`}
                                className="space-y-4"
                            >
                                {saveConflict ? (
                                    <div
                                        role="alert"
                                        className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between"
                                    >
                                        <span>{saveConflict.message}</span>
                                        <Button
                                            type="button"
                                            priorityType={UIPriority.SECONDARY}
                                            uiSize="compact"
                                            className="w-40 border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                                            onClick={() => void reloadSettings()}
                                            disabled={isLoading || isSaving}
                                        >
                                            Загрузить актуальные
                                        </Button>
                                    </div>
                                ) : null}
                                {renderActiveTab()}
                            </div>
                        </div>
                    </div>
                </div>

                {isActiveTabEditable ? (
                    <div className="border-t border-slate-200 p-[3px] flex justify-end gap-2">
                        <Button
                            type="button"
                            priorityType={UIPriority.SECONDARY}
                            onClick={() => resetForm(activeTab)}
                            className="w-24"
                            uiSize="compact"
                            disabled={isLoading || isSaving}
                        >
                            Очистить
                        </Button>
                        <Button
                            type="button"
                            priorityType={UIPriority.SECONDARY}
                            onClick={() => closeModal(activeTab)}
                            className="w-24"
                            uiSize="compact"
                            disabled={isSaving}
                        >
                            Отмена
                        </Button>
                        <Button
                            type="submit"
                            priorityType={UIPriority.PRIMARY}
                            className="w-28 disabled:opacity-50"
                            uiSize="compact"
                            disabled={isLoading || isSaving}
                        >
                            {isSaving ? 'Сохранение...' : 'Сохранить'}
                        </Button>
                    </div>
                ) : null}
                {renderPaymentDialog()}
            </form>
        </Modal>
    );
};
